import type { AssetEntry } from '../types';
import {
  ROAD_PATH,
  COMMERCIAL_PATH,
  INDUSTRIAL_PATH,
  SUBURBAN_PATH,
  FOREST_PATH,
  CHARACTER_PATH,
  CAR_PATH,
  NPC_CHARACTER_PATH,
} from '../config';
import { USED_BUILDING_KEYS } from '../world/CityMap';

// ─── Roads (Phase 1) ───
const roadModels = [
  'road-straight', 'road-bend', 'road-crossroad', 'road-intersection',
  'road-roundabout', 'road-end', 'road-bend-sidewalk', 'road-curve',
  'road-crossing', 'road-straight-barrier', 'road-end-round',
  'road-intersection-path', 'road-crossroad-path',
  'road-bend-square',
];

const roadDetails = [
  'light-square', 'light-square-double', 'light-square-cross',
  'sign-highway', 'construction-cone', 'construction-barrier',
  'tile-low',
];

// ─── Commercial (Phase 2) ───
const commercialBuildings = [
  'building-a', 'building-b', 'building-c', 'building-d',
  'building-e', 'building-f', 'building-g', 'building-h',
  'building-i', 'building-j', 'building-k', 'building-l',
  'building-m', 'building-n',
];
const commercialSkyscrapers = [
  'building-skyscraper-a', 'building-skyscraper-b',
  'building-skyscraper-c', 'building-skyscraper-d',
  'building-skyscraper-e',
];
const commercialDetails = [
  'detail-awning', 'detail-awning-wide',
  'detail-parasol-a', 'detail-parasol-b',
  'detail-overhang', 'detail-overhang-wide',
];
const commercialLOD = [
  'low-detail-building-a', 'low-detail-building-b', 'low-detail-building-c',
  'low-detail-building-d', 'low-detail-building-e', 'low-detail-building-f',
  'low-detail-building-g', 'low-detail-building-h', 'low-detail-building-i',
  'low-detail-building-j', 'low-detail-building-k', 'low-detail-building-l',
  'low-detail-building-m', 'low-detail-building-n',
];

// ─── Industrial (Phase 2) ───
const industrialBuildings = [
  'building-a', 'building-b', 'building-c', 'building-d',
  'building-e', 'building-f', 'building-g', 'building-h',
  'building-i', 'building-j', 'building-k', 'building-l',
  'building-m', 'building-n', 'building-o', 'building-p',
  'building-q', 'building-r', 'building-s', 'building-t',
];
const industrialDetails = [
  'chimney-basic', 'chimney-large', 'chimney-medium', 'chimney-small',
  'detail-tank',
];

// ─── Suburban (Phase 2) ───
const suburbanBuildings = [
  'building-type-a', 'building-type-b', 'building-type-c',
  'building-type-d', 'building-type-e', 'building-type-f',
  'building-type-g', 'building-type-h', 'building-type-i',
  'building-type-j', 'building-type-k', 'building-type-l',
  'building-type-m', 'building-type-n', 'building-type-o',
  'building-type-p', 'building-type-q', 'building-type-r',
  'building-type-s', 'building-type-t', 'building-type-u',
];
const suburbanDetails = [
  'fence', 'fence-low', 'fence-1x2', 'fence-2x2',
  'path-short', 'path-long', 'path-stones-short', 'path-stones-long',
  'driveway-short', 'driveway-long',
  'tree-large', 'tree-small', 'planter',
];

// ─── Forest (Phase 4) ───
const forestTrees = [
  'Tree_1_A_Color1', 'Tree_1_B_Color1', 'Tree_1_C_Color1',
  'Tree_2_A_Color1', 'Tree_2_B_Color1', 'Tree_2_C_Color1',
  'Tree_2_D_Color1', 'Tree_2_E_Color1',
  'Tree_3_A_Color1', 'Tree_3_B_Color1', 'Tree_3_C_Color1',
  'Tree_4_A_Color1', 'Tree_4_B_Color1', 'Tree_4_C_Color1',
];
const forestBushes = [
  'Bush_1_A_Color1', 'Bush_1_B_Color1', 'Bush_1_C_Color1',
  'Bush_1_D_Color1', 'Bush_1_E_Color1', 'Bush_1_F_Color1', 'Bush_1_G_Color1',
  'Bush_2_A_Color1', 'Bush_2_B_Color1', 'Bush_2_C_Color1',
  'Bush_2_D_Color1', 'Bush_2_E_Color1', 'Bush_2_F_Color1',
  'Bush_3_A_Color1', 'Bush_3_B_Color1', 'Bush_3_C_Color1',
  'Bush_4_A_Color1', 'Bush_4_B_Color1', 'Bush_4_C_Color1',
  'Bush_4_D_Color1', 'Bush_4_E_Color1', 'Bush_4_F_Color1',
];
const forestRocks = [
  'Rock_1_A_Color1', 'Rock_1_B_Color1', 'Rock_1_C_Color1',
  'Rock_1_D_Color1', 'Rock_1_E_Color1',
  'Rock_2_A_Color1', 'Rock_2_B_Color1', 'Rock_2_C_Color1',
  'Rock_3_A_Color1', 'Rock_3_B_Color1', 'Rock_3_C_Color1',
];
const forestGrass = [
  'Grass_1_A_Color1', 'Grass_1_B_Color1', 'Grass_1_C_Color1', 'Grass_1_D_Color1',
  'Grass_2_A_Color1', 'Grass_2_B_Color1', 'Grass_2_C_Color1', 'Grass_2_D_Color1',
];

// ─── Cars (Phase 3) ───
const carModels = [
  'sedan', 'sedan-sports', 'hatchback-sports',
  'suv', 'suv-luxury', 'taxi', 'police',
  'van', 'delivery', 'truck',
];

// ─── Character (Phase 1) ───
const characters = ['Meshy_AI_Meshy_Merged_Animations'];

// ─── NPC passengers (Phase 3) ───
// Full Kenney mini-character roster — one is picked at random per spawn so
// consecutive pickups and drop-offs look different.
const npcCharacters = [
  'character-male-a', 'character-male-b', 'character-male-c',
  'character-male-d', 'character-male-e', 'character-male-f',
  'character-female-a', 'character-female-b', 'character-female-c',
  'character-female-d', 'character-female-e', 'character-female-f',
];

// ─── Build manifest ───
function makeEntries(
  models: string[],
  basePath: string,
  category: AssetEntry['category'],
  phase: AssetEntry['phase'],
  ext = '.glb'
): AssetEntry[] {
  return models.map((name) => ({
    key: `${category}/${name}`,
    path: `${basePath}/${name}${ext}`,
    category,
    phase,
  }));
}

// Phase 2: Only load buildings the city grid actually uses (seeded RNG is deterministic)
const allBuildingEntries: AssetEntry[] = [
  ...makeEntries(commercialBuildings, COMMERCIAL_PATH, 'commercial', 2),
  ...makeEntries(commercialSkyscrapers, COMMERCIAL_PATH, 'commercial', 2),
  ...makeEntries(industrialBuildings, INDUSTRIAL_PATH, 'industrial', 2),
  ...makeEntries(suburbanBuildings, SUBURBAN_PATH, 'suburban', 2),
];
const usedBuildingEntries = allBuildingEntries.filter((e) => USED_BUILDING_KEYS.has(e.key));

export const ASSET_MANIFEST: AssetEntry[] = [
  // Phase 1: Roads + Character (blocking)
  ...makeEntries(roadModels, ROAD_PATH, 'road', 1),
  ...makeEntries(characters, CHARACTER_PATH, 'character', 1),
  // Phase 2: Only buildings used by the grid
  ...usedBuildingEntries,
  // Phase 3: Details + Cars + NPC passengers
  ...makeEntries(carModels, CAR_PATH, 'car', 3),
  ...makeEntries(npcCharacters, NPC_CHARACTER_PATH, 'npc', 3),
  ...makeEntries(roadDetails, ROAD_PATH, 'road', 3),
  ...makeEntries(commercialDetails, COMMERCIAL_PATH, 'commercial', 3),
  ...makeEntries(commercialLOD, COMMERCIAL_PATH, 'commercial', 3),
  ...makeEntries(industrialDetails, INDUSTRIAL_PATH, 'industrial', 3),
  ...makeEntries(suburbanDetails, SUBURBAN_PATH, 'suburban', 3),
  // Phase 4: Forest (GLTF not GLB)
  ...makeEntries(forestTrees, FOREST_PATH, 'forest', 4, '.gltf'),
  ...makeEntries(forestBushes, FOREST_PATH, 'forest', 4, '.gltf'),
  ...makeEntries(forestRocks, FOREST_PATH, 'forest', 4, '.gltf'),
  ...makeEntries(forestGrass, FOREST_PATH, 'forest', 4, '.gltf'),
];

export function getManifestByPhase(phase: number): AssetEntry[] {
  return ASSET_MANIFEST.filter((e) => e.phase === phase);
}

export function getManifestByCategory(category: AssetEntry['category']): AssetEntry[] {
  return ASSET_MANIFEST.filter((e) => e.category === category);
}

// Export named lists for direct use
export {
  carModels,
  forestTrees,
  forestBushes,
  forestRocks,
  forestGrass,
  commercialBuildings,
  commercialSkyscrapers,
  industrialBuildings,
  suburbanBuildings,
  suburbanDetails,
  commercialDetails,
  industrialDetails,
  commercialLOD,
  npcCharacters,
};
